import { SATOSHIS, toBitcoin } from 'lib/bsv-util'
import type {
    MemePreviewAnswer,
    MemePreviewMedia,
    MemeTopThread,
} from 'refactor/hooks/api/useGetMemes'

export type MemeValueKind = 'sats' | 'bitcoin' | 'usd'

export type MemeValuePresentation = {
    amount: string
    caption: string
    kind: MemeValueKind
}

export type MemeCardPreview = {
    answer: MemePreviewAnswer | null
    media: MemePreviewMedia | null
    upvalueTotalSats: number | null
}

export function formatMemeThreadCount(count: number): string {
    return `${count} thread${count === 1 ? '' : 's'}`
}

export function resolveMemeCardPreview(topThread?: MemeTopThread | null): MemeCardPreview {
    const winner = topThread?.winning_answer
    const answer = winner ?? topThread?.parent ?? null

    return {
        answer,
        media: winner?.media ?? null,
        upvalueTotalSats:
            winner?.upvalue_total_sats ??
            answer?.bsv_upvalue_amt ??
            topThread?.upvalue_total_sats ??
            null,
    }
}

function formatCompactSats(sats: number): string {
    if (sats >= 1_000_000) {
        return `${(sats / 1_000_000).toFixed(sats >= 10_000_000 ? 0 : 1)}M`
    }
    if (sats >= 1_000) return `${Math.round(sats / 1_000)}k`
    return `${sats}`
}

export function resolveMemeValuePresentation(
    value: number | null | undefined,
    useUsd: boolean,
    formatUsd: (sats: number) => string | null = () => null,
): MemeValuePresentation | null {
    if (value == null || !Number.isFinite(Number(value)) || Number(value) <= 0) return null
    const sats = Math.round(Number(value))

    if (useUsd) {
        const amount = formatUsd(sats)
        return amount ? { amount, caption: 'USD UPVALUED', kind: 'usd' } : null
    }

    if (sats >= SATOSHIS) {
        return {
            amount: toBitcoin(sats),
            caption: 'BITCOIN UPVALUED',
            kind: 'bitcoin',
        }
    }

    return {
        amount: formatCompactSats(sats),
        caption: 'SATS UPVALUED',
        kind: 'sats',
    }
}

export function formatMemeAge(
    timestamp: string | null | undefined,
    now = Date.now(),
    includeAgo = false,
): string | null {
    if (!timestamp) return null
    const createdAt = new Date(timestamp).getTime()
    if (!Number.isFinite(createdAt)) return null

    const elapsedMinutes = Math.max(0, Math.floor((now - createdAt) / 60_000))
    if (elapsedMinutes < 1) return includeAgo ? 'just now' : 'now'

    const elapsedHours = Math.floor(elapsedMinutes / 60)
    const elapsedDays = Math.floor(elapsedHours / 24)
    const compact =
        elapsedDays > 0
            ? `${elapsedDays}d`
            : elapsedHours > 0
            ? `${elapsedHours}h`
            : `${elapsedMinutes}m`
    return includeAgo ? `${compact} ago` : compact
}
